import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store';
import {
  AUTH_ACTIVITY_THROTTLE_MS,
  AUTH_SESSION_EXPIRED_EVENT,
  AUTH_STORAGE_KEYS,
  getLastActivityAt
} from '../../utils/authSession';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'focus'];
const SESSION_CHECK_MS = 15 * 1000;

const isLoginPath = (pathname) => pathname === '/login';

function AuthSessionManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const touchSession = useAuthStore((state) => state.touchSession);
  const syncLastActivity = useAuthStore((state) => state.syncLastActivity);
  const lastRecordedActivityRef = useRef(0);
  const lastNoticeReasonRef = useRef(null);
  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const redirectToLogin = useCallback((reason = 'idle_timeout') => {
    if (lastNoticeReasonRef.current !== reason) {
      lastNoticeReasonRef.current = reason;
      if (reason === 'idle_timeout') {
        toast.error('Session expired after 30 minutes of inactivity.');
      }
    }

    const currentLocation = locationRef.current;
    if (!isLoginPath(currentLocation.pathname)) {
      navigate('/login', {
        replace: true,
        state: {
          reason,
          noticeShown: reason === 'idle_timeout',
          from: currentLocation
        }
      });
    }
  }, [navigate]);

  const expireSession = useCallback((reason = 'idle_timeout') => {
    const state = useAuthStore.getState();
    if (state.isAuthenticated) {
      state.logout({ reason });
    }
    redirectToLogin(reason);
  }, [redirectToLogin]);

  const checkSession = useCallback(() => {
    const state = useAuthStore.getState();
    if (!state.isAuthenticated) return;

    if (!getLastActivityAt()) {
      state.touchSession();
      return;
    }

    if (state.isSessionExpired()) {
      expireSession('idle_timeout');
    }
  }, [expireSession]);

  const recordActivity = useCallback(() => {
    const state = useAuthStore.getState();
    if (!state.isAuthenticated) return;

    if (state.isSessionExpired()) {
      expireSession('idle_timeout');
      return;
    }

    const now = Date.now();
    if (now - lastRecordedActivityRef.current < AUTH_ACTIVITY_THROTTLE_MS) return;

    lastRecordedActivityRef.current = now;
    touchSession(now);
    lastNoticeReasonRef.current = null;
  }, [expireSession, touchSession]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    checkSession();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', checkSession);
    const intervalId = window.setInterval(checkSession, SESSION_CHECK_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
      document.removeEventListener('visibilitychange', checkSession);
      window.clearInterval(intervalId);
    };
  }, [checkSession, isAuthenticated, recordActivity]);

  useEffect(() => {
    const handleSessionExpired = (event) => {
      expireSession(event.detail?.reason || 'idle_timeout');
    };

    const handleStorage = (event) => {
      if (event.key === AUTH_STORAGE_KEYS.lastActivityAt) {
        syncLastActivity(Number(event.newValue) || null);
        checkSession();
        return;
      }

      if (event.key === AUTH_STORAGE_KEYS.store && !event.newValue) {
        const state = useAuthStore.getState();
        if (state.isAuthenticated) {
          state.logout({ callApi: false, reason: 'signed_out_elsewhere' });
          redirectToLogin('signed_out_elsewhere');
        }
      }
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener('storage', handleStorage);
    };
  }, [checkSession, expireSession, redirectToLogin, syncLastActivity]);

  return null;
}

export default AuthSessionManager;
